// @ts-strict
import { DocumentType, modelOptions, mongoose, PropType, post, pre, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { isDeviceVersionSelectorValid, sanitizeVersionSelector } from "../../../utils/index.js";
import {
    DeviceVersionSelectorString,
    getZodSchemaFromClass,
    ObjectIdString,
    validate,
} from "../../../zod-decorator/index.js";
import { AreaOfInterest } from "../../areaOfInterest/areaOfInterest.js";
import { DeviceVersion } from "../../commonSubSchemas/deviceVersion.js";
import { Device } from "../../device/device.js";
import { Organization } from "../../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    LoraNetworkProfileSupportedDeviceSelectorFieldsWithDefaults,
    LoraNetworkProfileSupportedDeviceSelectorSchemaTypes,
} from "../../types/loraNetworkProfileSupportedDeviceSelector.d.js";
import type { DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "LoraNetworkProfileSupportedDeviceSelector" as const;

async function sanitizeDeviceVersionSelectorIfModified(this: DocumentType<LoraNetworkProfileSupportedDeviceSelector>) {
    // Validate runs before save middleware in Mongoose, so sanitize at both phases to guarantee canonical values.
    if (this.isModified("deviceVersionSelector")) {
        this.deviceVersionSelector = sanitizeVersionSelector(this.deviceVersionSelector);
    }
}

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<LoraNetworkProfileSupportedDeviceSelector>>(...addSchemaKeyHook(SCHEMA_KEY))
@pre<LoraNetworkProfileSupportedDeviceSelector>("validate", sanitizeDeviceVersionSelectorIfModified)
@pre<LoraNetworkProfileSupportedDeviceSelector>("save", sanitizeDeviceVersionSelectorIfModified)
export class LoraNetworkProfileSupportedDeviceSelector extends SchemaBase<
    LoraNetworkProfileSupportedDeviceSelector,
    LoraNetworkProfileSupportedDeviceSelectorFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * -1 means this rule will never be assigned by default. When registering a new device, the highest priority
     * (largest priority number) profile that qualifies by all the selectors will be selected
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: true, default: -1 })
    public priority: number = -1;

    /**
     * Only devices that match this version selector will be able to receive this profile
     */
    @validate({ zodValidator: DeviceVersionSelectorString })
    @prop({
        type: () => String,
        required: true,
        validate: {
            validator: isDeviceVersionSelectorValid,
            message: (props) =>
                `Invalid device version selector: "${props.value}". Consult notion for device version selector syntax`,
        },
    })
    public deviceVersionSelector!: string;

    /**
     * If this is set, then only accounts in the list will see this preset as an option, and only devices registered
     * to those accounts will be able to receive this profile as a default.
     */
    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ ref: () => Organization, required: false }, PropType.ARRAY)
    public organizationSelector?: mongoose.Types.ObjectId[];

    /**
     * If this is set, then only devices in AOIs in this list will see this preset as an option
     */
    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ ref: () => AreaOfInterest, required: false }, PropType.ARRAY)
    public aoiSelector?: mongoose.Types.ObjectId[];

    static getters = {} as const satisfies Record<string, (self: LoraNetworkProfileSupportedDeviceSelector) => any>;

    static utilities = {
        doesDeviceSupportSelector: (
            self: LoraNetworkProfileSupportedDeviceSelector,
            device: Pick<Device, "version" | "owner" | "_aoi" | "schemaKey">,
        ): boolean => {
            if (
                !DeviceVersion.utilities.doesDeviceVersionMatchVersionSelector(
                    device.version,
                    self.deviceVersionSelector,
                )
            ) {
                return false;
            }
            if (
                self.organizationSelector &&
                self.organizationSelector.length > 0 &&
                !self.organizationSelector.map((a) => a.toString()).includes(device.owner.toString())
            ) {
                return false;
            }
            if (
                self.aoiSelector &&
                self.aoiSelector.length > 0 &&
                (!device._aoi || !self.aoiSelector.map((a) => a.toString()).includes(device._aoi.toString()))
            ) {
                return false;
            }
            return true;
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<LoraNetworkProfileSupportedDeviceSelectorSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<
            LoraNetworkProfileSupportedDeviceSelector,
            LoraNetworkProfileSupportedDeviceSelectorFieldsWithDefaults
        >,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

LoraNetworkProfileSupportedDeviceSelector.dtoValidators = getZodSchemaFromClass(
    LoraNetworkProfileSupportedDeviceSelector,
) as unknown as DtoValidatorTypes<LoraNetworkProfileSupportedDeviceSelectorSchemaTypes>;
